/**
 * PcmOpfsStore — streaming PCM recorder backed by the Origin Private
 * File System (OPFS). Lets a recording tool capture an arbitrarily long
 * take without ever holding the whole thing in the JS heap.
 *
 * WHY THIS EXISTS — the bug it fixes:
 *   /record (and any "record straight to a buffer" tool) accumulated raw
 *   Float32 PCM for the entire take in a JS array. One hour of stereo
 *   48 kHz audio is 48000 × 2 × 4 × 3600 ≈ 1.4 GB resident — and podcast
 *   / broadcast recording, the tool's headline use case, is exactly the
 *   long-form scenario. The tab OOM-crashes mid-take and the recording
 *   is lost silently.
 *
 *   screen-recorder already solved the analogous problem for video by
 *   streaming muxer output to OPFS (see public/js/screen-recorder/
 *   recorder.js). This module is the audio equivalent: capture chunks
 *   are converted to interleaved Int16 (half the bytes of Float32, and
 *   the recording is re-rendered through the FX chain at export anyway,
 *   so 16-bit on the captured-DRY intermediate is inaudible) and flushed
 *   to an OPFS file in batches. The heap only ever holds the pending
 *   batch (~256 KB), not the take.
 *
 *   Free bonus: because the take lives in a real file, a tab crash /
 *   accidental close no longer destroys it — listOrphans() surfaces it
 *   on the next visit for crash recovery.
 *
 * ⚠️⚠️ ЭТО ОБЕЩАНИЕ БЫЛО НЕВЕРНЫМ ДО 2026-09-01, и неверным ровно в том
 * случае, ради которого класс писался. `createWritable()` в Chromium пишет НЕ
 * в файл, а в swap-файл `.crswap`, и переносит данные в настоящий файл только
 * на `close()`. Замер настоящим перезапуском браузера с постоянным профилем
 * (запись 12 с):
 *
 *     createWritable()         во время записи  .pcm.crswap = 2048 КБ, .pcm = 0 КБ
 *                              после аварии     .pcm = 0 КБ  → предлагать нечего
 *     createSyncAccessHandle   во время записи  .pcm = 2048 КБ
 *                              после аварии     .pcm = 2048 КБ → дубль цел
 *
 * То есть «дубль лежит в файле» было правдой только после нормальной
 * остановки — а падение вкладки, единственный случай, от которого спасали,
 * уничтожало запись целиком. Теперь writer по умолчанию — sync access handle в
 * воркере (см. openSyncWritable ниже), с `flush()` после каждой порции;
 * `createWritable` остался ЗАПАСНЫМ путём.
 *
 * ⚠️ Проверять поддержку с главного потока нельзя:
 * `FileSystemFileHandle.prototype.createSyncAccessHandle` там `undefined` — он
 * существует только внутри воркера. Первая версия проверки поэтому всегда
 * отвечала «не поддерживается», и долговечный путь не включался ни разу.
 *
 * Сторож: tests/playwright/opfs-take-survives-a-crash.mjs (убивает браузер
 * посреди записи и требует, чтобы файл и предложение восстановить пережили).
 *
 * Metadata lives in the filename so no sidecar file can desync from the
 * data: `<prefix>_<epochMs>_<sampleRate>_<channels>.pcm`. The file body
 * is pure little-endian interleaved Int16 — sample count is derivable
 * from the file size.
 *
 * Public API (window.PcmOpfsStore):
 *   .isSupported()                         → bool
 *   .createRecorder({sampleRate, channels, prefix?}) → Promise<Recorder>
 *   .listOrphans(prefix?)                  → Promise<RecordingInfo[]>
 *   .openRecording(name)                   → Promise<Recording>
 *   .deleteRecording(name)                 → Promise<void>
 *   .cleanup(prefix?, keepNames?)          → Promise<void>
 *   ._pure                                 → pure helpers, for unit tests
 *
 * Recorder:
 *   .writeFloat32Interleaved(Float32Array)  enqueue a capture chunk
 *   .framesWritten()        → int (per-channel sample frames)
 *   .durationSeconds()      → number
 *   .finalize()             → Promise<RecordingInfo>   flush + close
 *   .abort()                → Promise<void>            close + delete
 *   .readAsAudioBuffer(ctx) → Promise<AudioBuffer>      (after finalize)
 *   .name                   → string
 *
 * Recording (from openRecording / a finalized Recorder):
 *   .meta  = {sampleRate, channels, frames, durationSeconds, bytes, name}
 *   .readAsAudioBuffer(ctx) → Promise<AudioBuffer>
 */
(function () {
'use strict';

var DEFAULT_PREFIX = '_timbrica_pcm';
// Flush whenever the pending Int16 batch reaches this many bytes. 256 KB
// at 48 kHz stereo Int16 is ~0.68 s of audio — a comfortable disk write
// granularity that keeps the heap footprint flat.
var FLUSH_BYTES = 256 * 1024;

// ── Pure helpers (no OPFS, no DOM) — unit-tested directly in node ─────
var Pure = {
    // Float32 sample → clamped little-endian Int16 value.
    floatToInt16Value: function (x) {
        var s = x < -1 ? -1 : (x > 1 ? 1 : x);
        return s < 0 ? Math.round(s * 0x8000) : Math.round(s * 0x7FFF);
    },
    // Float32Array → Int16Array, same length, clamped.
    floatToInt16: function (f32) {
        var n = f32.length;
        var out = new Int16Array(n);
        for (var i = 0; i < n; i++) {
            var s = f32[i];
            s = s < -1 ? -1 : (s > 1 ? 1 : s);
            out[i] = s < 0 ? (s * 0x8000) : (s * 0x7FFF);
        }
        return out;
    },
    // Int16Array → Float32Array in [-1, 1].
    int16ToFloat32: function (i16) {
        var n = i16.length;
        var out = new Float32Array(n);
        for (var i = 0; i < n; i++) {
            var v = i16[i];
            out[i] = v < 0 ? v / 0x8000 : v / 0x7FFF;
        }
        return out;
    },
    // Deinterleave an interleaved Int16Array into `channels` Float32Arrays.
    deinterleave: function (i16, channels) {
        var frames = Math.floor(i16.length / channels);
        var out = [];
        for (var c = 0; c < channels; c++) out.push(new Float32Array(frames));
        for (var i = 0; i < frames; i++) {
            for (var c2 = 0; c2 < channels; c2++) {
                var v = i16[i * channels + c2];
                out[c2][i] = v < 0 ? v / 0x8000 : v / 0x7FFF;
            }
        }
        return out;
    },
    buildName: function (prefix, sampleRate, channels) {
        return (prefix || DEFAULT_PREFIX) + '_' + Date.now() + '_' +
            (sampleRate | 0) + '_' + (channels | 0) + '.pcm';
    },
    // Parse a name built by buildName back into metadata, or null if the
    // name does not match (so foreign OPFS files are never touched).
    parseName: function (name, prefix) {
        var pfx = prefix || DEFAULT_PREFIX;
        if (name.indexOf(pfx + '_') !== 0 || name.slice(-4) !== '.pcm') return null;
        var body = name.slice(pfx.length + 1, -4);
        var parts = body.split('_');
        if (parts.length !== 3) return null;
        var ts = parseInt(parts[0], 10);
        var sr = parseInt(parts[1], 10);
        var ch = parseInt(parts[2], 10);
        if (!(ts > 0) || !(sr > 0) || !(ch > 0) || ch > 8) return null;
        return { timestamp: ts, sampleRate: sr, channels: ch };
    },
};

function isSupported() {
    return typeof navigator !== 'undefined' &&
        navigator.storage &&
        typeof navigator.storage.getDirectory === 'function';
}

/* ── ДОЛГОВЕЧНЫЙ ПИСАТЕЛЬ: sync access handle в воркере ────────────────────
   ⚠️⚠️ ЗАЧЕМ. Докблок выше обещал: «because the take lives in a real file, a
   tab crash no longer destroys it». Обещание было НЕВЕРНЫМ, и это доказано
   замером 2026-09-01 (настоящий перезапуск браузера с постоянным профилем,
   запись 12 с):

       createWritable()        во время записи: .pcm.crswap = 2048 КБ,
                                                .pcm = 0 КБ
                               после аварии:    .pcm = 0 КБ, предложить нечего
       createSyncAccessHandle  во время записи: .bin = 2048 КБ (видно снаружи)
                               после аварии:    .bin = 2048 КБ — ЦЕЛО

   Причина: `createWritable()` в Chromium пишет в swap-файл `.crswap` и
   переносит данные в настоящий файл только на `close()`. То есть весь смысл
   «дубль лежит в файле» пропадал ровно в том случае, ради которого класс и
   писался — при падении вкладки. `createSyncAccessHandle()` пишет в файл
   напрямую, а `flush()` доводит до диска.

   Плата: sync-хэндл запрещён на главном потоке в Chromium, поэтому здесь
   поднимается свой воркер (тот же приём, что в opfs-writer.js). Одна порция —
   одно сообщение с передачей буфера, без копирования.

   ⚠️ Запасной путь остаётся: если воркеров или sync-хэндлов нет, работает
   прежний `createWritable`. Он быстрее и на главном потоке, но НЕ переживает
   аварию — поэтому он именно запасной, а не предпочтительный.
   (В opfs-writer.js приоритет обратный, и это правильно ТАМ: он пишет готовый
   блоб одним махом, ему нечего терять между вызовами.) */
var SYNC_WORKER_SRC = [
    'let ah=null,off=0;',
    'self.onmessage=async(e)=>{',
    ' const m=e.data;',
    ' try{',
    '  if(m.op==="open"){',
    '   const root=await navigator.storage.getDirectory();',
    '   const h=await root.getFileHandle(m.name,{create:true});',
    '   ah=await h.createSyncAccessHandle(); off=0;',
    '   self.postMessage({id:m.id,ok:true});',
    '  }else if(m.op==="write"){',
    '   const v=new Uint8Array(m.buf);',
    '   ah.write(v,{at:off}); off+=v.length; ah.flush();',
    '   self.postMessage({id:m.id,ok:true});',
    '  }else if(m.op==="close"){',
    '   if(ah){ah.flush();ah.close();ah=null;}',
    '   self.postMessage({id:m.id,ok:true});',
    '  }',
    ' }catch(err){ self.postMessage({id:m.id,ok:false,err:String(err&&err.message||err)}); }',
    '};'
].join(String.fromCharCode(10));

/* ⚠️ Проверять `FileSystemFileHandle.prototype.createSyncAccessHandle` с
   ГЛАВНОГО потока бессмысленно: в Chromium его там нет вовсе (замер — метод
   `undefined` в window, существует только внутри воркера). Первая версия этой
   проверки поэтому всегда отвечала «не поддерживается», и долговечный путь не
   включался НИ РАЗУ — при том что сам путь работал. Поэтому проверяем то, что
   видно снаружи, а пригодность выясняем попыткой: неудачное открытие падает в
   `catch` и включает прежний `createWritable`. */
function syncWriterSupported() {
    return typeof Worker === 'function' &&
        typeof navigator !== 'undefined' && navigator.storage &&
        typeof navigator.storage.getDirectory === 'function';
}

/* Обёртка над воркером с тем же лицом, что у FileSystemWritableFileStream:
   write(buffer) / close() / abort(). Так точка замены в рекордере — одна. */
function openSyncWritable(name) {
    return new Promise(function (resolve, reject) {
        var w;
        try {
            w = new Worker(URL.createObjectURL(new Blob([SYNC_WORKER_SRC], { type: 'text/javascript' })));
        } catch (e) { reject(e); return; }
        var seq = 0, waiting = {};
        w.onmessage = function (e) {
            var cb = waiting[e.data.id];
            if (!cb) return;
            delete waiting[e.data.id];
            e.data.ok ? cb.res() : cb.rej(new Error(e.data.err || 'opfs-worker'));
        };
        w.onerror = function (e) { reject(e); };
        function call(msg, transfer) {
            return new Promise(function (res, rej) {
                msg.id = ++seq; waiting[msg.id] = { res: res, rej: rej };
                w.postMessage(msg, transfer || []);
            });
        }
        call({ op: 'open', name: name }).then(function () {
            resolve({
                write: function (buf) { return call({ op: 'write', buf: buf }, [buf]); },
                close: function () { return call({ op: 'close' }).then(function () { w.terminate(); }); },
                abort: function () { return call({ op: 'close' }).catch(function () {}).then(function () { w.terminate(); }); },
            });
        }, function (e) { try { w.terminate(); } catch (_) {} reject(e); });
    });
}

function getRoot() {
    return navigator.storage.getDirectory();
}

// Decode an OPFS file (pure interleaved Int16) into an AudioBuffer using
// the caller's AudioContext (works with OfflineAudioContext too).
function fileToAudioBuffer(file, meta, audioCtx) {
    return file.arrayBuffer().then(function (ab) {
        var i16 = new Int16Array(ab);
        var ch = meta.channels;
        var frames = Math.floor(i16.length / ch);
        var buf = (audioCtx && typeof audioCtx.createBuffer === 'function')
            ? audioCtx.createBuffer(ch, Math.max(1, frames), meta.sampleRate)
            : null;
        if (!buf) throw new Error('PcmOpfsStore: no AudioContext to decode into');
        for (var c = 0; c < ch; c++) {
            var dst = buf.getChannelData(c);
            for (var i = 0; i < frames; i++) {
                var v = i16[i * ch + c];
                dst[i] = v < 0 ? v / 0x8000 : v / 0x7FFF;
            }
        }
        return buf;
    });
}

function makeRecordingInfo(name, meta, bytes) {
    var frames = Math.floor(bytes / 2 / meta.channels);
    return {
        name: name,
        sampleRate: meta.sampleRate,
        channels: meta.channels,
        bytes: bytes,
        frames: frames,
        durationSeconds: frames / meta.sampleRate,
        timestamp: meta.timestamp || null,
    };
}

function createRecorder(opts) {
    opts = opts || {};
    var sampleRate = opts.sampleRate | 0;
    var channels = opts.channels | 0;
    var prefix = opts.prefix || DEFAULT_PREFIX;
    if (!(sampleRate > 0) || !(channels > 0)) {
        return Promise.reject(new Error('PcmOpfsStore: sampleRate/channels required'));
    }
    if (!isSupported()) {
        return Promise.reject(new Error('PcmOpfsStore: OPFS unavailable'));
    }

    var name = Pure.buildName(prefix, sampleRate, channels);
    var meta = { sampleRate: sampleRate, channels: channels };

    /* ⚠️ Просим ПОСТОЯННОЕ хранилище — иначе браузер вправе выселить файл
       прямо во время записи. Это не предосторожность «на всякий случай», а
       наш собственный замеренный случай: у screen-recorder Яндекс.Браузер
       агрессивно вычищал best-effort OPFS под нехваткой места, и файл
       8–17-минутной записи исчезал МЕЖДУ открытием и закрытием — 4 обращения
       за 7 дней, все на Яндексе (см. комментарий у persist() в
       screen-recorder/recorder.js). Долговечная запись без этого — половина
       решения: данные доходят до диска и оттуда же пропадают.
       Fire-and-forget: отказ ничего не ломает, запись идёт своим ходом. */
    try {
        if (navigator.storage && typeof navigator.storage.persist === 'function') {
            navigator.storage.persisted().then(function (already) {
                if (!already) return navigator.storage.persist();
            }).catch(function () {});
        }
    } catch (_) {}

    return getRoot().then(function (root) {
        return root.getFileHandle(name, { create: true }).then(function (handle) {
            var openWritable = syncWriterSupported()
                ? openSyncWritable(name).catch(function () { return handle.createWritable(); })
                : handle.createWritable();
            return Promise.resolve(openWritable).then(function (writable) {
                // pending: a list of Int16Array batches not yet flushed.
                var pending = [];
                var pendingBytes = 0;
                var totalBytes = 0;       // bytes committed to the file
                var totalSamples = 0;     // interleaved Int16 samples written
                var aborted = false, finalized = false;
                // writeChain serialises createWritable() writes so chunks
                // land in order even though write() is async.
                var writeChain = Promise.resolve();
                var writeError = null;

                function flush() {
                    if (pendingBytes === 0) return;
                    var batch = pending;
                    var batchBytes = pendingBytes;
                    pending = [];
                    pendingBytes = 0;
                    // Concatenate the batch into one buffer for a single
                    // write() call (fewer, larger writes = less overhead).
                    var merged = new Int16Array(batchBytes / 2);
                    var off = 0;
                    for (var i = 0; i < batch.length; i++) {
                        merged.set(batch[i], off);
                        off += batch[i].length;
                    }
                    totalBytes += batchBytes;
                    writeChain = writeChain.then(function () {
                        if (aborted) return;
                        return writable.write(merged.buffer);
                    }).catch(function (e) {
                        writeError = e;
                    });
                }

                var rec = {
                    name: name,
                    meta: meta,

                    writeFloat32Interleaved: function (f32) {
                        if (aborted || finalized) return;
                        if (!f32 || !f32.length) return;
                        var i16 = Pure.floatToInt16(f32);
                        pending.push(i16);
                        pendingBytes += i16.length * 2;
                        totalSamples += i16.length;
                        if (pendingBytes >= FLUSH_BYTES) flush();
                    },

                    framesWritten: function () {
                        return Math.floor(totalSamples / channels);
                    },
                    durationSeconds: function () {
                        return (totalSamples / channels) / sampleRate;
                    },

                    finalize: function () {
                        if (finalized) return Promise.resolve(makeRecordingInfo(name, meta, totalBytes));
                        finalized = true;
                        flush();
                        return writeChain.then(function () {
                            return writable.close();
                        }).then(function () {
                            if (writeError) throw writeError;
                            return makeRecordingInfo(name, meta, totalBytes);
                        });
                    },

                    abort: function () {
                        aborted = true;
                        pending = []; pendingBytes = 0;
                        return writeChain.then(function () {
                            return writable.abort ? writable.abort() : writable.close();
                        }).catch(function () {}).then(function () {
                            return deleteRecording(name);
                        }).catch(function () {});
                    },

                    readAsAudioBuffer: function (audioCtx) {
                        if (!finalized) {
                            return Promise.reject(new Error('PcmOpfsStore: finalize() before readAsAudioBuffer()'));
                        }
                        return root.getFileHandle(name).then(function (h) {
                            return h.getFile();
                        }).then(function (file) {
                            return fileToAudioBuffer(file, meta, audioCtx);
                        });
                    },
                };
                return rec;
            });
        });
    });
}

function listOrphans(prefix) {
    if (!isSupported()) return Promise.resolve([]);
    return getRoot().then(function (root) {
        var found = [];
        var walk = root.values ? root.values() : null;
        if (!walk) return [];
        function next() {
            return walk.next().then(function (res) {
                if (res.done) return found;
                var entry = res.value;
                if (entry.kind === 'file') {
                    var m = Pure.parseName(entry.name, prefix);
                    if (m) {
                        return entry.getFile().then(function (file) {
                            found.push(makeRecordingInfo(entry.name,
                                { sampleRate: m.sampleRate, channels: m.channels, timestamp: m.timestamp },
                                file.size));
                            return next();
                        }).catch(next);
                    }
                }
                return next();
            });
        }
        return next();
    }).catch(function () { return []; });
}

function openRecording(name, prefix) {
    if (!isSupported()) return Promise.reject(new Error('PcmOpfsStore: OPFS unavailable'));
    var m = Pure.parseName(name, prefix);
    if (!m) return Promise.reject(new Error('PcmOpfsStore: not a recording file: ' + name));
    return getRoot().then(function (root) {
        return root.getFileHandle(name).then(function (h) { return h.getFile(); });
    }).then(function (file) {
        var meta = { sampleRate: m.sampleRate, channels: m.channels, timestamp: m.timestamp };
        return {
            meta: makeRecordingInfo(name, meta, file.size),
            readAsAudioBuffer: function (audioCtx) {
                return fileToAudioBuffer(file, meta, audioCtx);
            },
        };
    });
}

function deleteRecording(name) {
    if (!isSupported()) return Promise.resolve();
    return getRoot().then(function (root) {
        return root.removeEntry(name);
    }).catch(function () {});
}

// Delete every recording file for `prefix` except those in keepNames.
// Called at tool init to sweep orphans the user chose not to recover.
function cleanup(prefix, keepNames) {
    var keep = {};
    (keepNames || []).forEach(function (n) { keep[n] = 1; });
    return listOrphans(prefix).then(function (list) {
        return Promise.all(list.map(function (info) {
            return keep[info.name] ? Promise.resolve() : deleteRecording(info.name);
        }));
    });
}

window.PcmOpfsStore = {
    isSupported: isSupported,
    createRecorder: createRecorder,
    listOrphans: listOrphans,
    openRecording: openRecording,
    deleteRecording: deleteRecording,
    cleanup: cleanup,
    _pure: Pure,
    version: '1.0.0',
};

})();
